export const PASSWORD_RESET_FINISH_PATH = '/login/forgot-password/finish'
export const PASSWORD_RESET_STORAGE_KEY = 'password-reset'

let inMemoryCredentials = null

export function isPasswordResetFinishPath (path = '') {
  return path.replace(/\/$/, '') === PASSWORD_RESET_FINISH_PATH
}

function readToken (hash, query = {}) {
  const fromHash = (hash || '').replace(/^#/, '')
  return fromHash || query.token || null
}

function persistCredentials (token, request) {
  if (!token || !request) {
    return false
  }

  const credentials = {
    request,
    token
  }

  try {
    window.sessionStorage.setItem(PASSWORD_RESET_STORAGE_KEY, JSON.stringify(credentials))
    inMemoryCredentials = null
    return true
  } catch {
    inMemoryCredentials = credentials
    return true
  }
}

/**
 * Persist reset credentials and strip them from window.location.
 * Runs from plugins/password-reset-finish.client.js before PostHog init and GTM init.
 */
export function persistAndStripPasswordResetLocation (location = window.location, history = window.history) {
  if (!location || !isPasswordResetFinishPath(location.pathname)) {
    return false
  }

  const params = new URLSearchParams(location.search)
  const query = {
    request: params.get('request'),
    token: params.get('token')
  }
  persistCredentials(readToken(location.hash, query), query.request)

  if (!location.hash && !params.has('request') && !params.has('token')) {
    return false
  }

  params.delete('request')
  params.delete('token')
  const search = params.toString()
  history.replaceState(history.state, '', location.pathname + (search ? `?${search}` : ''))
  return true
}

export function getRedirectForPasswordResetRoute (to) {
  if (!to || !isPasswordResetFinishPath(to.path)) {
    return null
  }

  persistCredentials(readToken(to.hash, to.query), to.query && to.query.request)

  const hasSecret = Boolean(to.hash) || (to.query && (to.query.request != null || to.query.token != null))
  if (!hasSecret) {
    return null
  }

  const query = { ...to.query }
  delete query.request
  delete query.token

  return {
    path: to.path,
    query,
    hash: '',
    replace: true
  }
}

export function getPasswordResetCredentials () {
  try {
    const raw = window.sessionStorage.getItem(PASSWORD_RESET_STORAGE_KEY)
    if (raw) {
      return JSON.parse(raw)
    }
  } catch {}

  return inMemoryCredentials
}

export function takePasswordResetCredentials () {
  const credentials = getPasswordResetCredentials()
  clearPasswordResetCredentials()
  return credentials
}

export function clearPasswordResetCredentials () {
  inMemoryCredentials = null

  try {
    window.sessionStorage.removeItem(PASSWORD_RESET_STORAGE_KEY)
  } catch {}
}

/**
 * Self-contained so Nuxt can serialize it into Bugsnag.start() config.
 * Strips password-reset secrets from event, breadcrumb, and session payloads.
 */
export function redactPasswordResetSecretsForBugsnag (target) {
  if (!target) {
    return
  }

  function strip (value) {
    if (!value || typeof value !== 'string') {
      return value
    }

    const methodMatch = value.match(/^([A-Z]+)\s+(\S+)/)
    const method = methodMatch ? methodMatch[1] : null
    const rawUrl = methodMatch ? methodMatch[2] : value

    try {
      const isAbsolute = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(rawUrl)
      const parsed = new URL(rawUrl, 'https://invalid.invalid')
      if (parsed.pathname.replace(/\/$/, '') !== '/login/forgot-password/finish') {
        return value
      }

      parsed.searchParams.delete('request')
      parsed.searchParams.delete('token')
      parsed.hash = ''

      const stripped = isAbsolute
        ? parsed.origin + parsed.pathname + parsed.search
        : parsed.pathname + parsed.search

      return method ? method + ' ' + stripped : stripped
    } catch (e) {
      return value
    }
  }

  function redact (item) {
    if (!item) {
      return
    }

    if (item.request && typeof item.request.url === 'string') {
      item.request.url = strip(item.request.url)
    }

    if (typeof item.context === 'string') {
      item.context = strip(item.context)
    }

    if (item.metadata && typeof item.metadata === 'object') {
      ['from', 'to', 'request', 'url'].forEach(function (key) {
        if (typeof item.metadata[key] === 'string') {
          item.metadata[key] = strip(item.metadata[key])
        }
      })
    }
  }

  redact(target)

  if (Array.isArray(target.breadcrumbs)) {
    target.breadcrumbs.forEach(redact)
  }
}
